import React, {
  useContext, useCallback, useRef, useEffect, useState
} from 'react';
import PropTypes, {
  bool, func, shape, string
} from 'prop-types';
import { Alert as SUIAlert } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { PIPCalculator } from '@thd-olt-component-react/calculator';
import { Alert } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Row, Col } from '@thd-olt-component-react/grid';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';
import {
  Fulfillment,
  isShowBoth,
  isTwoTile,
  isTwoTileOff,
  isTwoTileQuantityUpdateCookie,
  isBackorderedSelection
} from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';
import { SubscribeAndSave } from '@thd-olt-component-react/subscribe-and-save';
import { useDomPath, useLifeCycleEventBus } from '@thd-olt-functional/utils';

import classNames from 'classnames';
import {
  getDeliveryZip,
  isSthAndInStock,
  fencingItemsGroup,
  checkIfUserIsInStore
} from '../util';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxUnavailable } from './subcomponents/BuyboxUnavailable';
import { BuyboxLite } from './subcomponents/BuyboxLite';
import { BuyboxActions } from './subcomponents/BuyboxActions';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import { useBuyBox, useBuyboxGetData } from './hooks/useBuyBox';
import * as helper from '../BuyboxHelper';
import { track } from '../track';
import { dataModel } from './dataModel';
import './buybox.scss';
import { FULFILLMENT_METHOD_STH } from '../constants';

/**
 * Buybox container
 */
const Buybox = (props) => {
  const {
    channel,
    isServer
  } = useContext(ExperienceContext);

  const buyboxTimerRef = useRef(new Date());

  // use this ref to prevent showing loading placeholder after initial render
  const initialLoadingRef = useRef(true);

  const {
    stash,
    hideQuantity,
    disableATC: buyboxDisableATC,
    disableSTH,
    disableBOSS,
    onClickPassThrough,
    lite,
    sticky,
    hideInstantCheckout,
    hidePaypalCheckout,
    hideProReferral,
    showCards,
    directData,
    showFavoriteAndQuote,
    noGrid,
    renderCalculator
  } = props;

  let { itemId } = props;

  const covidAlert = useConfigService('fs-prop:buy-area__buybox--covidAlert');
  const bopisMinimumThreshold = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThreshold');
  const bopisMinimumThresholdStores = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThresholdStores');
  const bodfsMinimumThreshold = useConfigService('fs-prop:buy-area__buybox--bodfsMinimumThreshold');
  const bopisHolidayTiming = useConfigService('fs-prop:buy-area__buybox--bopisHolidayTiming');

  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const isTwoTileEnabled = useConfigService('fs-prop:buy-area__buybox--isTwoTileEnabled');
  const isTwoTileQuantityUpdate = useConfigService('isTwoTileQuantityUpdate') || isTwoTileQuantityUpdateCookie();
  const isRisMessageEnabled = useConfigService('fs:isRisMessageEnabled');

  const isTwoTileEnabledFS = (
    (isTwoTileEnabledGlobal && isTwoTileEnabled)
    || ((isTwoTile() || isShowBoth()) && !isTwoTileOff())
  );

  const getItemIdFromConfigurator = (items) => {
    if (items) {
      return items.find((item) => item.isAnchor).itemId;
    }
    return '';
  };

  const { storeId, storeZip } = useStore();
  const [buyboxState, setBuyboxState] = useBuyBox({ deliveryZipCode: getDeliveryZip() || storeZip });
  const [updatedQuantity, setQuantityToQuery] = useState(buyboxState.quantity || 1);
  const [subscriptionFrequency, setSubscriptionFrequency] = useState(null);
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
  }
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  useEffect(() => {
    // call to set quantity with a delay
    const timer = setTimeout(() => {
      const isQuantityUpdateActivate = !buyboxState.fallbackMode && isTwoTileEnabledFS && isTwoTileQuantityUpdate;
      // Update the quantity after a wait so as to avoid unnecessary re-renders.
      if (isQuantityUpdateActivate && !buyboxState.quantityExceeded) {
        setQuantityToQuery(buyboxState.quantity);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [buyboxState.quantity, buyboxState.quantityExceeded, buyboxState.fallbackMode]);

  const { data, loading, error } = useBuyboxGetData({
    itemId,
    storeId,
    directData,
    quantity: isTwoTileEnabledFS && isTwoTileQuantityUpdate ? updatedQuantity : 1,
    zipCode: buyboxState.zipCode
  });

  useEffect(() => {
    if (data?.product) {
      if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
        const start = buyboxTimerRef.current.getTime();
        const end = new Date().getTime();
        LIFE_CYCLE_EVENT_BUS.trigger('buybox.loaded', { start, end });
      }
    }
  }, [data?.product]);

  const {
    fulfillment = {},
    info = {},
    availabilityType = {},
    pricing = {},
    specificationGroup = [],
    identifiers = {},
  } = data?.product || {};
  const { fallbackMode = false } = fulfillment || {};
  const parentId = identifiers?.parentId || '';
  const { hasSubscription, minimumOrderQuantity, quantityLimit = 0 } = info || {};

  useEffect(() => {
    if (fulfillment?.fulfillmentOptions) {
      const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, buyboxState.fulfillmentMethod);
      const maximumInventoryQuantity = isBackorderedApplied ? 9999
        : helper.getFulfillmentMaxQuantity({ fulfillment, isTwoTileEnabledFS, qty: buyboxState.quantity });
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantityExceeded: buyboxState.quantity > maximumInventoryQuantity,
          fallbackMode: fulfillment?.fallbackMode || false,
        };
      });
    }
  }, [fulfillment]);

  useEffect(() => {
    const fencingItemMessage = fencingItemsGroup.some((skuclassification) => {
      return skuclassification.test(info?.productDepartment + '-' + info?.classNumber + '-' + info?.subClassNumber);
    });
    if (fencingItemMessage) {
      checkIfUserIsInStore().then((isUserInStore) => {
        setBuyboxState((prevState) => {
          return {
            ...prevState,
            showFencingMessage: isUserInStore
          };
        });
      });
    }
  }, [info]);

  const onQuantityChange = useCallback(({ quantity }) => {
    const qty = parseInt(quantity, 10);

    const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, buyboxState.fulfillmentMethod);
    // get the inventory available quantity for selected fulfillment
    const maximumInventoryQuantity = isBackorderedApplied ? 9999
      : helper.getFulfillmentMaxQuantity({ fulfillment, isTwoTileEnabledFS, qty });

    if (!Number.isNaN(qty) && qty > 0) {
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantity: qty,
          quantityExceeded: qty > maximumInventoryQuantity
        };
      });
    }
  }, [buyboxState]);

  const onFulfillmentChange = ({
    currentFulfillment,
    method,
    isShowATC,
    disableATC,
    isOutOfStockOnline,
    quantityLimitExceed,
    availableFulfillments,
    isItemOOS
  }) => {
    const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, method);
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        fulfillmentMethod: method,
        availableFulfillments,
        isShowATC,
        disableATC,
        isOutOfStockOnline,
        currentFulfillment,
        quantityLimitExceed,
        // Reset Subscribe & save on fulfillment change
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: method === FULFILLMENT_METHOD_STH ? subscriptionFrequency : null
          }
        },
        quantityExceeded: isBackorderedApplied ? quantityLimitExceed : prevState.quantityExceeded,
        isItemOOS
      };
    });
  };

  const onProductAddOnChange = (addOnData) => {
    const { addOn, flags } = helper.getSelectedAddOnInfo(addOnData, buyboxState.fulfillmentMethod);
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        ...flags,
        productAddOns: {
          ...prevState.productAddOns,
          ...addOn
        }
      };
    });
  };

  const onSubscribeAndSaveChange = ({ frequency, isOptIn }) => {
    // isOptIn will be true if they have selected to subscribe
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: isOptIn ? frequency : null,
          }
        }
      };
    });
    setSubscriptionFrequency(isOptIn ? frequency : null);
  };

  useEffect(() => {
    const { coverage, addAdditional } = buyboxState;

    if (!coverage) return;
    const updatedCoverage = addAdditional ? +coverage + (+coverage * 0.10) : coverage;
    const quantity = Math.ceil(updatedCoverage / +pricing?.alternate?.unit?.unitsPerCase);
    const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, buyboxState.fulfillmentMethod);
    const maximumInventoryQuantity = isBackorderedApplied ? 9999
      : helper.getFulfillmentMaxQuantity({ fulfillment, isTwoTileEnabledFS, quantity });

    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity: quantity > 9999 ? 9999 : quantity,
        calculatorQuantity: quantity,
        quantityExceeded: quantity > maximumInventoryQuantity
      };
    });
  }, [pricing?.alternate?.unit?.unitsPerCase]);

  const onChangeCalculator = ({ addAdditionalCoverage, quantity, coverage }) => {
    const isBackorderedApplied = isBackorderedSelection(fulfillment?.backordered, buyboxState.fulfillmentMethod);
    const maximumInventoryQuantity = isBackorderedApplied ? 9999
      : helper.getFulfillmentMaxQuantity({ fulfillment, isTwoTileEnabledFS, quantity });
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity: quantity > 9999 ? 9999 : quantity,
        calculatorQuantity: quantity,
        coverage,
        quantityExceeded: quantity > maximumInventoryQuantity,
        addAdditional: addAdditionalCoverage
      };
    });
  };
  // ######## End of Calculator methods ##########
  const onDeliveryOptionsZipCodeChange = useCallback((shippingDataModel) => {
    const { zipCode } = shippingDataModel;
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        zipCode
      };
    });
  }, []);

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', { ...atcStash, parentId });
    }
  }, [itemId, parentId]);

  const unavailable = buyboxDisableATC; // From prop
  const {
    isOutOfStockOnline,
    isShowATC,
    quantity,
    quantityLimitExceed,
    zipCode,
    fulfillmentMethod,
    paypalEligible,
    productAddOns,
    availableFulfillments,
    assembleItemAtStore,
    assembleItemAtHome,
    quantityExceeded,
    isItemOOS,
  } = buyboxState;
  const isATCDisabled = unavailable || buyboxState?.disableATC;
  const showSubscription = (hasSubscription && !isTwoTileEnabledFS)
    || (hasSubscription && isTwoTileEnabledFS && isSthAndInStock({ availableFulfillments, isOutOfStockOnline }));

  const { dualPath = false, virtualAssistance = false, category } = productAddOns;

  const addOnsAssemblyAndMore = helper.getAddOnsFlagsForFssDrawer({
    productAddOns,
    assembleItemAtStore,
    assembleItemAtHome,
    fulfillmentMethod,
    availableFulfillments
  });

  const orderQuantity = minimumOrderQuantity > quantity ? minimumOrderQuantity : quantity;

  const [pathForAdvisoryMessage, refForAdvisoryMessage] = useDomPath();

  if (error && !data) {
    return (
      <BuyboxError />
    );
  }
  const currentSkuGrouping = info?.productDepartment + '-' + info?.classNumber + '-' + info?.subClassNumber;

  const risMessageEligible = info?.isInStoreReturnMessageEligible;

  const twoTilesWithQtyIsLoading = !!(isTwoTileEnabledFS && isTwoTileQuantityUpdate && initialLoadingRef.current);
  const regular2or3TilesIsLoading = (!isTwoTileQuantityUpdate && loading);

  if (twoTilesWithQtyIsLoading || regular2or3TilesIsLoading || isServer || storeId === '8119') {
    // only for 2tile + qty update: if graphql data is loaded after first page render, stop showing loaders
    if (twoTilesWithQtyIsLoading && data?.product && !loading) {
      initialLoadingRef.current = false;
    }

    // Do Not remove this as we want to show the loader when loading state is true
    if (loading) {
      return (
        <BuyboxLoader
          showCards
          lite={lite}
          noGrid={noGrid}
          tilesCount={isTwoTileEnabledFS ? 2 : 3}
        />
      );
    }
  }

  if (!data?.product) {
    return null;
  }

  if (unavailable) {
    return (
      <BuyboxUnavailable
        hideQuantity={hideQuantity}
        itemId={itemId}
        quantity={orderQuantity}
        quantityLimitExceed={quantityLimitExceed}
        onQuantityChange={onQuantityChange}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        directData={directData}
      />
    );
  }

  if (lite) {
    return (
      <BuyboxLite
        itemId={itemId}
        configurableItems={items}
        unavailable={unavailable}
        covidAlert={covidAlert}
        storeId={storeId}
        isOutOfStockOnline={isOutOfStockOnline}
        isShowATC={isShowATC}
        storeZip={storeZip}
        isATCDisabled={isATCDisabled}
        channel={channel}
        zipCode={zipCode}
        fulfillmentMethod={fulfillmentMethod}
        fulfillment={fulfillment}
        paypalEligible={paypalEligible}
        availabilityType={availabilityType}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        bopisHolidayTiming={bopisHolidayTiming}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
        directData={directData}
        isTwoTileEnabledFS={isTwoTileEnabledFS}
        quantityLimit={quantityLimit}
      />
    );
  }
  const inventoryWarningMessage = (isTwoTileEnabledFS && quantityLimit > 0 && quantity > quantityLimit)
    ? `This item has a limit of ${quantityLimit} per order.`
    : !isOutOfStockOnline
    && isShowATC
    && !!minimumOrderQuantity
    && `This item can only be purchased for quantities of ${minimumOrderQuantity} or more`;

  const wrapperClasses = classNames('buybox-wrapper', { 'buybox-wrapper--card-background': showCards });
  const cardClasses = classNames({ 'buybox-top__wrapper': !showCards, buybox__card: showCards });
  const WrapperTag = Row;

  return (
    <WrapperTag>
      <Col fallback="12" nopadding={showCards} className={wrapperClasses} data-component="Buybox">
        <div className={cardClasses}>
          {!unavailable && !virtualAssistance && (
            /* eslint-disable-next-line no-sequences */
            <div className={showCards ? '' : 'buybox__fulfillment'}>
              <Fulfillment
                itemId={itemId}
                covidAlert={covidAlert}
                quantity={orderQuantity}
                onChange={onFulfillmentChange}
                onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                dualPath={dualPath}
                category={category}
                bodfsMinimumThreshold={bodfsMinimumThreshold}
                bopisMinimumThreshold={bopisMinimumThreshold}
                bopisMinimumThresholdStores={bopisMinimumThresholdStores}
                bopisHolidayTiming={bopisHolidayTiming}
                isTwoTileEnabledFS={isTwoTileEnabledFS}
                directData={directData}
                quantityExceeded={quantityExceeded}
                addOnsAssemblyAndMore={addOnsAssemblyAndMore}
                disableSTH={disableSTH}
                disableBOSS={disableBOSS}
              />
            </div>
          )}
        </div>
        {/* Removed the isOutOfStockOnline here to display calculator section */}
        {!isItemOOS && !dualPath && renderCalculator && (
          <ErrorBoundary>
            <div className={cardClasses} data-testid={`pip-calculator-${itemId}`}>
              {renderCalculator(itemId, onChangeCalculator)}
            </div>
          </ErrorBoundary>
        )}

        <>
          <ProductAddOns
            itemId={itemId}
            fulfillment={buyboxState.fulfillmentMethod}
            onChange={onProductAddOnChange}
            availableFulfillments={availableFulfillments}
            hideProReferral={hideProReferral}
            showCards={showCards}
            defaultProps={buyboxState.productAddOns}
            isTwoTileEnabledFS={isTwoTileEnabledFS}
            zipCode={zipCode}
            // Please do not remove the below prop isTwoTileEnabled={isTwoTileEnabledFS}
            // as ProductAddOns has no prop mapping for isTwoTileEnabledFS
            // and still uses isTwoTileEnabled
            isTwoTileEnabled={isTwoTileEnabledFS}
            isShowATC={isShowATC}
          />
          {/* Removed the isOutOfStockOnline here to display subscribeAndSave section */}
          {showSubscription && isShowATC && (
            <div className={cardClasses}>
              <SubscribeAndSave
                itemId={itemId}
                quantity={orderQuantity}
                fulfillment={buyboxState.fulfillmentMethod}
                onChange={onSubscribeAndSaveChange}
              />
            </div>
          )}
        </>

        <div className={cardClasses}>
          {!!inventoryWarningMessage && (
            <>
              {!!pathForAdvisoryMessage.length && track('advisory-message', {
                message: inventoryWarningMessage,
                path: pathForAdvisoryMessage
              })}
              <div className="alert-inline alert-inline--warning inventory--warning" ref={refForAdvisoryMessage}>
                <span>{inventoryWarningMessage}</span>
              </div>
            </>
          )}
          {isRisMessageEnabled && risMessageEligible && !isItemOOS && isShowATC && !dualPath && !virtualAssistance && (
            <>
              <Row>
                <Col className="alert-inline--info">
                  <Alert type="information" inline message="This item is only returnable in store." />
                </Col>
              </Row>
            </>
          )}
          {buyboxState.showFencingMessage && (
            <>
              <Row>
                <Col className="alert-inline--info">
                  <SUIAlert
                    status="warning"
                  >
                    If unavailable, please see local Pro Desk for pricing and availability.
                  </SUIAlert>
                </Col>
              </Row>
            </>
          )}
          {/* Removed the isOutOfStockOnline here to display BuyboxActions section */}
          {!isItemOOS && isShowATC && !dualPath && !virtualAssistance && (
            <BuyboxActions
              alterBrowserHistory
              disableATC={isATCDisabled}
              fulfillment={fulfillment}
              fulfillmentMethod={fulfillmentMethod}
              hideQuantity={hideQuantity}
              isAssembleItemAtStore={assembleItemAtStore}
              itemId={itemId}
              itemPrice={pricing?.value}
              configurableItems={items}
              onClickPassThrough={onClickPassThrough}
              minimumOrderQuantity={minimumOrderQuantity}
              onATCClick={onATCClick}
              onQuantityChange={onQuantityChange}
              paypalEligible={paypalEligible}
              productAddOns={productAddOns}
              quantity={orderQuantity}
              quantityLimitExceed={quantityLimitExceed}
              sticky={sticky}
              storeId={storeId}
              storeZip={storeZip}
              zipCode={zipCode}
              showCards={showCards}
              showFavoriteAndQuote={showFavoriteAndQuote}
              isTwoTileEnabledFS={isTwoTileEnabledFS}
              maxQuantityExceeded={quantityExceeded}
              quantityLimit={quantityLimit}
              fallbackMode={isTwoTileEnabledFS ? fallbackMode : false}
              hidePaypalCheckout={hidePaypalCheckout}
              hideInstantCheckout={hideInstantCheckout}
            />
          )}
        </div>
      </Col>
    </WrapperTag>
  );
};

Buybox.displayName = 'Buybox';
Buybox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  /** will hide the buybox quantity box */
  hideQuantity: bool,
  /** Pass ddo values to override the default */
  disableATC: bool,
  /**
   * if the buybox is wrapped in a "grid" class, defaults to false.
   * Fusion and sui will utilize this and set it to true.
   */
  noGrid: bool,
  onClickPassThrough: func,
  /**
   * Disable the STH tile regardless of fulfillment data.
   */
  disableSTH: bool,
  disableBOSS: bool,
  lite: bool,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  sticky: bool,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  hideProReferral: bool,
  showCards: bool,
  showFavoriteAndQuote: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any),
  renderCalculator: func
};

Buybox.defaultProps = {
  itemId: null,
  lite: false,
  hideQuantity: false,
  disableATC: false,
  onClickPassThrough: null,
  noGrid: false,
  disableSTH: false,
  disableBOSS: false,
  stash: {},
  sticky: false,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  hideProReferral: false,
  showCards: false,
  showFavoriteAndQuote: false,
  directData: null,
  renderCalculator: null
};

Buybox.dataModel = extend(
  dataModel,
  Fulfillment,
  ProductAddOns,
  SubscribeAndSave,
  InstantCheckout,
  AddToQuote,
  AddToList,
  PIPCalculator
);

export default Buybox;
